
import React from 'react';
import PYQMultiplier from '@/components/quiz/PYQMultiplier';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BookCopy, LucideWand2, Brain, Target, Clock, Sparkles } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from '@/hooks/use-mobile';

const PYQMultiplierPage: React.FC = () => {
  const { isMobile, isSmallMobile, screenSize } = useIsMobile();
  
  return (
    <div className={`container mx-auto py-3 sm:py-6 ${isMobile ? 'px-1 sm:px-2' : 'px-4'} pb-24 md:pb-6`}>
      <Card className="mb-3 sm:mb-6 border-none shadow-sm w-full">
        <CardHeader className={`${isMobile ? 'px-2 py-2 sm:px-3 sm:py-3' : 'px-4 py-4'}`}>
          <div className="flex items-center space-x-2">
            <BookCopy className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            <CardTitle className={`${isSmallMobile ? 'text-base' : isMobile ? 'text-lg' : 'text-2xl sm:text-3xl'} font-bold`}>
              Adaptive PYQ Multiplier
            </CardTitle>
          </div>
          <CardDescription className={`text-xs ${isMobile ? '' : 'sm:text-sm'}`}>
            Generate personalized variations of past year questions with AI. The system adapts to your performance and learning patterns.
          </CardDescription>
        </CardHeader>
        <CardContent className={`${isMobile ? 'px-2 pb-2' : 'px-4 pb-4'}`}>
          <div className="bg-muted/20 rounded-lg p-2 sm:p-4 border border-dashed">
            <div className="flex items-center mb-2">
              <LucideWand2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 text-primary" />
              <h3 className="font-medium text-sm sm:text-base">Key Features:</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-4 mt-2">
              <div className="flex items-start">
                <Brain className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 text-primary mt-0.5" />
                <div>
                  <h4 className="font-medium text-xs sm:text-sm">Adaptive Learning</h4>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    Questions adapt to your performance level
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Target className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 text-primary mt-0.5" />
                <div>
                  <h4 className="font-medium text-xs sm:text-sm">Topic Targeting</h4>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    Track performance by topic to identify weak areas
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 text-primary mt-0.5" />
                <div>
                  <h4 className="font-medium text-xs sm:text-sm">Spaced Practice</h4>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    Practice concepts at optimal intervals
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Tabs defaultValue="generate" className="space-y-3 sm:space-y-6">
        <TabsList className={`grid w-full grid-cols-2 ${isSmallMobile ? 'text-xs' : ''}`}>
          <TabsTrigger value="generate">Generate Questions</TabsTrigger>
          <TabsTrigger value="performance">Performance Analytics</TabsTrigger>
        </TabsList>
        
        <TabsContent value="generate" className="space-y-3 sm:space-y-4">
          <PYQMultiplier />
        </TabsContent>
        
        <TabsContent value="performance" className="space-y-3 sm:space-y-4">
          <Card>
            <CardHeader className={`${isMobile ? 'p-2' : 'p-4'}`}>
              <div className="flex items-center">
                <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-primary" />
                <CardTitle className="text-sm sm:text-base">Your Learning Analytics</CardTitle>
              </div>
              <CardDescription className="text-xs">
                Track your performance and see how the system adapts to your learning patterns
              </CardDescription>
            </CardHeader>
            <CardContent className={`${isMobile ? 'p-3' : 'p-6'}`}>
              <div className="text-center p-2 sm:p-6 text-muted-foreground text-xs sm:text-sm">
                <p>Complete more quizzes to see your personalized analytics here</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PYQMultiplierPage;
